import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Nfc, ShieldCheck, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Public NFC resolver page.
 *
 * Mounted at `/nfc/:tagId`. When an NFC sticker is scanned, the consumer's
 * browser opens this URL. We call the public backend resolver, which:
 *   - looks up the batch bound to the tag,
 *   - increments the scan count + records an audit event,
 *   - returns the redirect target.
 *
 * We then forward the consumer to /verify/:batchId?via=nfc so the existing
 * customer-facing verification flow renders with the NFC-verified badge.
 *
 * No auth required — the physical sticker is the trust factor.
 */
const NfcResolve = () => {
  const { tagId } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/nfc/${encodeURIComponent(tagId)}`);
        if (cancelled) return;
        if (data?.batch_id) {
          navigate(`/verify/${encodeURIComponent(data.batch_id)}?via=nfc`, { replace: true });
        } else {
          setError('No batch bound to this NFC tag yet.');
        }
      } catch (e) {
        if (cancelled) return;
        const status = e?.response?.status;
        const msg =
          status === 404
            ? `No batch is currently bound to NFC tag ${tagId}.`
            : 'Could not resolve this NFC tag. Please try again.';
        setError(msg);
      }
    })();
    return () => { cancelled = true; };
  }, [tagId, navigate]);

  return (
    <div className="min-h-screen flex flex-col bg-slate-50" data-testid="nfc-resolve-page">
      <header className="bg-emerald-700 text-white py-8 sm:py-10 px-4 sm:px-6">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 rounded-full backdrop-blur-sm mb-3">
            <Nfc className="w-4 h-4" />
            <span className="text-xs font-semibold tracking-wider uppercase">PureSustain NFC Verification</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: 'Manrope' }}>
            Verifying product authenticity…
          </h1>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-12">
        {error ? (
          <div className="max-w-md w-full bg-white border border-amber-200 rounded-2xl p-6 sm:p-8 text-center shadow-sm">
            <AlertCircle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
            <h2 className="text-lg font-semibold text-slate-800 mb-2" data-testid="nfc-resolve-error-title">
              NFC tag not recognised
            </h2>
            <p className="text-sm text-slate-600 leading-relaxed mb-4">{error}</p>
            <p className="text-xs text-slate-400">Tag ID: <span className="font-mono">{tagId}</span></p>
          </div>
        ) : (
          <div className="text-center" data-testid="nfc-resolve-loading">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100 mb-4 animate-pulse">
              <ShieldCheck className="w-8 h-8 text-emerald-700" />
            </div>
            <p className="text-sm text-slate-600">
              Cross-checking the supply chain record for <span className="font-mono text-slate-800">{tagId}</span>…
            </p>
            <p className="text-xs text-slate-400 mt-2">You will be redirected automatically.</p>
          </div>
        )}
      </main>
    </div>
  );
};

export default NfcResolve;
